/*
 * Every colour here is a design token. The shadow geometry is literal — it has one
 * consumer, so it stays local until a second one wants it.
 */

.base {
  /* Spread gives a zero-size box something to cast; blur softens it. Kept tight on
     purpose — the reference's 22px/12px was tuned for a dark-only product and washes
     over two lines of text on a light surface. */
  --scroll-shadow-blur: 12px;
  --scroll-shadow-spread: 7px;

  /* --shadow-color carries the per-theme strength: black in both, weaker on light,
     because the same alpha that reads as depth on a dark surface is a smudge on a
     pale one. Lightening the shadow instead reads as a glow rather than depth. */
  --scroll-shadow-color: var(--shadow-color);

  /* How much scrolling it takes to fade a shadow fully in or out. */
  --scroll-shadow-distance: 40px;
}

/*
 * The shadow is sticky rather than an opaque overlay, which is the whole trick: a
 * box-shadow darkens whatever is underneath, so this never needs to know the background
 * colour. Sticky pins it to the scroll edge with no positioning context to set up.
 */
.base::before,
.base::after {
  content: '';
  display: block;
  position: sticky;
  z-index: var(--z-sticky);
  pointer-events: none;
  box-shadow: 0 0 var(--scroll-shadow-blur) var(--scroll-shadow-spread) var(--scroll-shadow-color);
  /* Hidden unless a scroll timeline drives it — see the @supports block. Without this,
     a browser that drops animation-timeline would fall back to a time-based animation
     and park both shadows at their end state. */
  opacity: 0;
}

/* ── Vertical ── */

.y {
  overflow-y: auto;
}

/* Zero height, so the box is invisible and only its shadow shows. Width comes for free:
   a block-level sticky child fills the container. */
.y::before,
.y::after {
  height: 0;
}

.y::before {
  top: 0;
}

.y::after {
  bottom: 0;
}

/* ── Horizontal ── */

/* Flex so the shadows can stretch to the content's height — there's no other way to
   make a zero-width sticky child full-height in an auto-height container. Verified it
   doesn't change how a table lays out or overflows. */
.x {
  display: flex;
  overflow-x: auto;
}

.x::before,
.x::after {
  flex: 0 0 0;
  align-self: stretch;
  width: 0;
}

.x::before {
  left: 0;
}

.x::after {
  right: 0;
}

/* ── Driving it ── */

@keyframes scrollShadowIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scrollShadowOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

/* The leading shadow fades in over the first stretch of scrolling; the trailing one
   fades out over the last. Both are off at rest when there's nothing to scroll, because
   a timeline with no scrollable overflow sits at progress 0. */
/*noinspection CssUnknownProperty*/
@supports (animation-timeline: scroll()) {
  .y::before {
    animation: scrollShadowIn linear both;
    animation-timeline: scroll(nearest block);
    animation-range: 0 var(--scroll-shadow-distance);
  }

  .y::after {
    animation: scrollShadowOut linear both;
    animation-timeline: scroll(nearest block);
    animation-range: calc(100% - var(--scroll-shadow-distance)) 100%;
  }

  .x::before {
    animation: scrollShadowIn linear both;
    animation-timeline: scroll(nearest inline);
    animation-range: 0 var(--scroll-shadow-distance);
  }

  .x::after {
    animation: scrollShadowOut linear both;
    animation-timeline: scroll(nearest inline);
    animation-range: calc(100% - var(--scroll-shadow-distance)) 100%;
  }
}
